/* @jsx h */
import { defineComponent, h, ref, computed } from "vue";
import { RouterLink, useRouter, useRoute } from "vue-router";
import lemmonLogo from "@app/images/branding/lemmon-logo-full.svg?raw";
import {
  ResponsiveNavbar,
  useLocalization,
  LemmonSwitcher,
} from "@lemmonhq/uikit";
import { DesktopNav } from "./DesktopNav";
import { MobileNav } from "./MobileNav";
import { QuickLinks } from "./QuickLinks";
import { useAuth } from "@app/vue/providers/Auth";
import { RestaurantInfoModal } from "./RestaurantInfoModal";
import { GroupNavbar } from "../../GroupShell/GroupNavbar";
import { MobileTabBar } from "./MobileTabBar";
import { isMobileTabBarPrototypeEnabled } from "./mobileTabBarPrototype";

const PosNavbar = defineComponent({
  name: "PosNavbar",
  setup() {
    const router = useRouter();
    const route = useRoute();
    const auth = useAuth();
    const posMode = auth.posMode;
    const isSidebarOpen = ref(false);
    const { t } = useLocalization();
    const isRestaurantInfoModalOpen = ref(false);
    const useMobileTabBar = isMobileTabBarPrototypeEnabled();

    const currentMobileView = computed(() => {
      const path = route.path;
      if (path.includes("/pos")) return "pos";
      if (path.includes("/tables")) return "tables";
      return "pos";
    });
    const onPOSModeToggle = () => {
      posMode.value = !posMode.value;
      router.push(posMode.value ? "/app/pos" : "/app/reports");
    };
    const renderLogo = () => (
      <div class="flex justify-center items-center space-x-2 xl:space-x-4">
        <RouterLink to="/app/pos" class="flex justify-center items-center px-0">
          <div
            data-env={(import.meta.env.VITE_ENV as string).toLocaleLowerCase()}
            class="logo-container relative"
            style="height: 25px; width:100px"
            v-html={lemmonLogo}
          />
        </RouterLink>
        {/* POS/Manager segmented control — iOS style */}
        {auth.user.value?.role === "manager" && (
          <LemmonSwitcher
            value={posMode.value ? "pos" : "manager"}
            onChange={(value) => {
              const nextPosMode = value === "pos";
              if (nextPosMode !== posMode.value) {
                onPOSModeToggle();
              }
            }}
            className="flex-shrink-0 !h-[30px]"
            buttonClassName="px-3 text-[11px] font-medium tracking-wide"
            labelClassName="hidden"
            iconClassName="text-[15px]"
            indicatorClassName="top-[2px] bottom-[2px]"
            options={[
              {
                value: "pos",
                icon: "point_of_sale",
                label: "POS",
                mobileLabel: "POS",
                ariaLabel: "POS",
              },
              {
                value: "manager",
                icon: "manage_accounts",
                label: "Manager",
                mobileLabel: "Manager",
                ariaLabel: "Manager",
              },
            ]}
          />
        )}
      </div>
    );

    const renderDesktopNav = () => <DesktopNav posMode={posMode.value} />;

    const renderMobileNav = () => (
      <MobileNav
        posMode={posMode.value}
        onLinkClick={() => {
          isSidebarOpen.value = false;
          // This will be called by ResponsiveNavbar to close the mobile menu
          return true;
        }}
      />
    );

    const renderQuickLinks = () => (
      <QuickLinks
        onRestaurantInfoOpen={() => (isRestaurantInfoModalOpen.value = true)}
      />
    );

    return () => {
      return (
        <>
          <div
            style={
              {
                //height: `calc(64px + var(--safe-area-top, 0px))`,
              }
            }
            class={[
              "flex flex-col",
              useMobileTabBar ? "max-[698px]:hidden" : "",
            ]}
          >
            <ResponsiveNavbar
              isOpen={isSidebarOpen}
              renderLogo={renderLogo}
              renderQuickLinks={() => renderQuickLinks()}
              renderDesktopNav={renderDesktopNav}
              renderMobileNav={renderMobileNav}
              rendreMobileShortcuts={() => {
                if (!auth.user.value) {
                  return null;
                }
                return (
                  <div class="flex gap-4 px-4">
                    <LemmonSwitcher
                      value={currentMobileView.value}
                      onChange={(val) => {
                        if (val === "pos") {
                          router.push("/app/pos");
                        } else if (val === "tables") {
                          router.push("/app/tables");
                        }
                      }}
                      iconOnlyOnMobile={false}
                      className="w-full"
                      buttonClassName="gap-1 p-2 text-xs font-medium"
                      iconClassName="text-lg"
                      options={[
                        {
                          value: "pos",
                          icon: "computer",
                          label: "",
                          mobileLabel: "",
                        },
                        {
                          value: "tables",
                          icon: "table_restaurant",
                          label: "",
                          mobileLabel: "",
                        },
                      ]}
                    />
                  </div>
                );
              }}
            />
          </div>

          {useMobileTabBar ? <MobileTabBar /> : null}

          {/* Restaurant Info Modal */}
          {isRestaurantInfoModalOpen.value && (
            <RestaurantInfoModal
              onClose={() => (isRestaurantInfoModalOpen.value = false)}
            />
          )}
        </>
      );
    };
  },
});

type NavbarProps = {
  mode?: "pos" | "group";
};

export const Navbar = defineComponent<NavbarProps>({
  name: "Navbar",
  props: ["mode"],
  setup(props) {
    return () => (props.mode === "group" ? <GroupNavbar /> : <PosNavbar />);
  },
});

export * from "./NavLink";
export * from "./NavGroup";
